import * as yup from "yup";
import axios from "../../config/axios";
let reviewImageRequirement = {
  allowedExtensions: ["image/png", "image/jpg", "image/jpeg"],
  size: 1024 * 1024 * 2,
};
let jobFileRequirement = {
  allowedExtensions: [
    "application/pdf",
    "application/msword", // .doc
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  ],
  size: 1024 * 1024 * 2, // 2 MB
};

export const imageSizeCheck = yup.addMethod(
  yup.mixed,
  "imageSizeCheck",
  function (allowedSizeInBytes) {
    return this.test("image-size-check", async function (value) {
      //console.log("validating imageSizeCheck", value);
      const { path, createError } = this;
      //console.log(typeof value)
      if (!value || typeof value === "string") {
        return true;
      }

      if (value.size > allowedSizeInBytes) {
        let sizeInMb = allowedSizeInBytes / (1024 * 1024);
        return createError({
          path,
          message: `Uploaded file is too big, only ${sizeInMb} MB allowed.`,
        });
      }

      return true;
    });
  }
);

export const imageTypeCheck = yup.addMethod(
  yup.mixed,
  "imageTypeCheck",
  function (allowedTypes) {
    return this.test("image-type-check", async function (value) {
      //console.log("validating imageTypeCheck");
      //console.log(typeof value)
      if (!value || typeof value === "string") {
        return true;
      }

      const { path, createError } = this;

      if (!value) {
        return;
      }

      if (!allowedTypes.includes(value.type)) {
        let extensions = allowedTypes.map((type) => type.replace("image/", ""));
        return createError({
          path,
          message: `Uploaded file extension is not allowed.`,
        });
      }

      return true;
    });
  }
);
export const contactUsSchema = yup.object().shape({
  name: yup.string().required("Please enter your full name"),
  email: yup
    .string()
    .trim()
    .required("Please enter your email address")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
      "Please enter a valid email address"
    ),
  country: yup.string().required("Please select your country"),
  mobile: yup
    .string()
    .required("Please enter your phone number")
    .min(10, "Please enter your complete phone number"),
  message: yup
    .string()
    .required("Please enter message")
    .max(300, "maximum 300 characters allowed"),
  // recaptcha: yup.string().required("Captcha is required"),
});
export const signInSchema = yup.object().shape({
  email: yup
    .string()
    .trim()
    .required("Please enter your email address")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
      "Please enter a valid email address"
    ),
  password: yup
    .string()
    .required("Please enter your password")
    .min(8, "Password must be at least 8 characters"),
});

export const childSchema = yup.object().shape({
  firstName: yup.string().required("Please enter your child's first name"),
  lastName: yup.string().required("Please enter your child's last name"),
  age: yup
    .number()
    .typeError("Please enter a number")
    .required("Please enter your child's age")
    .min(1, "Age must be at least 1"),
  gender: yup.string().required("Please select your child's gender"),
  schoolName: yup.string(),
});

export const addressSchema = yup.object().shape({
  firstName: yup.string().required("Please enter receiver's first name"),
  lastName: yup.string().required("Please enter receiver's last name"),
  email: yup
    .string()
    .trim()
    .required("Please enter receiver's email address")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
      "Please enter a valid email address"
    ),

  mobile: yup
    .string()
    .required("Please enter receiver's phone number")
    .min(10, "Please enter your complete phone number"),
  area: yup.string().required("Please enter receiver's area"),
  city: yup.string().required("Please select receiver's city"),
  country: yup.string().required("Please enter receiver's country"),
  address: yup.string().required("Please enter receiver's address"),
  landmark: yup.string().required("Please enter a landmark"),
  isDefault: yup.string().oneOf(["0", "1"]), // optional
});

export const cardSchema = yup.object().shape({
  cardHolderName: yup
    .string()
    .trim()
    .required("Please enter card holder's name"),
  cardNumber: yup
    .string()
    .required("Please enter card number")
    .min(16, "Card number must be 16 digits")
    .max(16, "Card number must be 16 digits")
    .matches(/^[0-9]{13,19}$/, "Please enter a valid card number"),
  expiryMonth: yup
    .string()
    .required("Please enter expiry month")
    .matches(/^(0[1-9]|1[0-2])$/, "Please enter a valid month"),
  expiryYear: yup
    .string()
    .required("Please enter expiry year")
    .matches(/^[0-9]{2}$/, "Please enter last two digits of the year")
    .test("notExpired", "Please enter valid card details", function (value) {
      if (!value) return false;
      const { expiryMonth } = this.parent;

      const expMonth = parseInt(expiryMonth, 10);
      const expYear = parseInt(value, 10);

      const today = new Date();
      const currentYear = today.getFullYear() % 100;
      const currentMonth = today.getMonth() + 1;

      return (
        expYear > currentYear ||
        (expYear === currentYear && expMonth >= currentMonth)
      );
    }),

  cvv: yup
    .string()
    .required("Please enter CVV")
    .matches(/^[0-9]{3,4}$/, "Please enter valid CVV"),

  isSaved: yup.string().oneOf(["0", "1"], "Invalid save option"),
});

export const reviewSchema = yup.object().shape({
  name: yup.string().required("Please enter your full name"),
  email: yup
    .string()
    .trim()
    .required("Please enter your email address")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
      "Please enter a valid email address"
    ),
  mobile: yup
    .string()
    .required("Please enter your phone number")
    .min(10, "Please enter your complete phone number"),
  message: yup
    .string()
    .required("Please enter message")
    .max(300, "maximum 300 characters allowed"),
  image: yup
    .mixed()
    .imageTypeCheck(reviewImageRequirement.allowedExtensions)
    .imageSizeCheck(reviewImageRequirement.size),
  // recaptcha: yup.string().required("Captcha is required"),
});
export const schoolSchema = yup.object().shape({
  schoolNurseryName: yup.string().required("Please enter school/nursery name"),
  contactPersonName: yup
    .string()
    .required("Please enter contact person's name"),
  contactPersonRole: yup
    .string()
    .required("Please enter contact person's role"),
  email: yup
    .string()
    .trim()
    .required("Please enter your email address")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
      "Please enter a valid email address"
    ),
  mobile: yup
    .string()
    .required("Please enter your phone number")
    .min(10, "Please enter your complete phone number"),
  numberOfChildren: yup
    .number()
    .typeError("Please enter a number")
    .required("Please enter number of children"),
  address: yup.string().required("Please enter address"),
  city: yup.string().required("Please enter city"),
  message: yup
    .string()
    .required("Please enter message")
    .max(300, "maximum 300 characters allowed"),
});
export const partySchema = yup.object().shape({
  parentName: yup.string().required("Please enter your full name"),
  email: yup
    .string()
    .trim()
    .required("Please enter your email address")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
      "Please enter a valid email address"
    ),
  mobile: yup
    .string()
    .required("Please enter your phone number")
    .min(10, "Please enter your complete phone number"),
  eventDate: yup.string().required("Please enter event date"),
  eventTime: yup.string().required("Please enter event time"),
  address: yup.string().required("Please enter address"),
  city: yup.string().required("Please enter city"),
  occasion: yup.string().required("Please enter occasion"),
  numberOfChildren: yup
    .number()
    .typeError("Please enter a number")
    .required("Please enter number of children"),

  message: yup
    .string()
    .required("Please enter message")
    .max(300, "maximum 300 characters allowed"),
});
export const jobSchema = yup.object().shape({
  firstName: yup.string().required("Please enter your first name"),
  lastName: yup.string().required("Please enter your last name"),
  email: yup
    .string()
    .trim()
    .required("Please enter your email address")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
      "Please enter a valid email address"
    ),
  mobile: yup
    .string()
    .required("Please enter your phone number")
    .min(10, "Please enter your complete phone number"),
  message: yup
    .string()
    .required("Please enter message")
    .max(300, "maximum 300 characters allowed"),
  noticePeriod: yup
    .number()
    .typeError("Please enter a number")
    .required("Please enter your notice period"),
  file: yup
    .mixed()
    .required("Please add your resume")
    .imageTypeCheck(jobFileRequirement.allowedExtensions)
    .imageSizeCheck(jobFileRequirement.size),
  // recaptcha: yup.string().required("Captcha is required"),
});

let lastCheckedEmail = "";
let lastResult = true;

export const signupSchema = yup.object().shape({
  firstName: yup.string().required("Please enter your first name"),
  lastName: yup.string().required("Please enter your last name"),
  nationality: yup.string().required("Please select your nationality"),

  email: yup
    .string()
    .trim()
    .required("Please enter your email address")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i,
      "Please enter a valid email address"
    )
    .test(
      "check-email-exists",
      "This email is already registered",
      async function (value) {
        if (!value) return true;

        // ❗ Check format FIRST before calling API
        const isValidFormat = /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(
          value
        );
        if (!isValidFormat) return true; // format error already shown

        // ✔ Avoid repeated API calls
        if (value === lastCheckedEmail) return lastResult;

        try {
          const res = await axios.post(
            "/dashboard/customers/is-email-registered",
            { email: value }
          );

          lastCheckedEmail = value;

          // already registered → validation fail
          lastResult = !res.data.emailAlreadyRegistered;

          return lastResult;
        } catch (err) {
          console.error("Error checking email:", err);
          return true; // allow submission if API fails
        }
      }
    ),

  confirmEmail: yup
    .string()
    .trim()
    .required("Please confirm your email address")
    .oneOf([yup.ref("email"), null], "Email addresses must match"),

  mobile: yup
    .string()
    .required("Please enter your phone number")
    .min(10, "Please enter your complete phone number"),

  password: yup
    .string()
    .required("Please enter your password")
    .min(8, "Password must be at least 8 characters"),

  confirmPassword: yup
    .string()
    .required("Please confirm your password")
    .oneOf([yup.ref("password"), null], "Passwords must match"),
});

export const personalInfoSchema = yup.object().shape({
  firstName: yup.string().required("Please enter your first name"),
  lastName: yup.string().required("Please enter your last name"),
  nationality: yup.string().required("Please select your nationality"),
  mobile: yup
    .string()
    .required("Please enter your phone number")
    .min(10, "Please enter your complete phone number"),
});
// dashboard orders

export const productReviewSchema = yup.object().shape({
  rating: yup
    .number()
    .min(1, "Please give a rating")
    .required("Rating is required"),
  feedback: yup.string(),
});

export const productReturnSchema = yup.object().shape({
  reason: yup.string().required("Please enter a reason for return"),
  message: yup.string().required("Please enter a message").max(300, "maximum 300 characters allowed"),
  file: yup
    .mixed()
    .required("Please add proof for return")
    .imageTypeCheck(reviewImageRequirement.allowedExtensions)
    .imageSizeCheck(reviewImageRequirement.size),
});

export const cancelSchema = yup.object().shape({
  reason: yup.string().required("Please select a reason for cancellation"),
  customReason: yup.string(),
  confirm: yup.boolean().oneOf([true], "Please confirm the cancellation"),
});
export const cancelMealSchema = yup.object().shape({
  reason: yup.string().required("Please enter reason for cancellation"),
});
export const dateChangeSchema = yup.object().shape({
  startDate: yup.date().required("Please select start date"),
});
export const expressCheckoutPersonalSchema = yup.object().shape({
  firstName: yup.string().required("Please enter your first name"),
  lastName: yup.string().required("Please enter your last name"),
  email: yup
    .string()
    .trim()
    .required("Please enter your email address")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
      "Please enter a valid email address"
    ),
  mobile: yup
    .string()
    .required("Please enter your phone number")
    .min(10, "Please enter your complete phone number"),
  agreeToCreateAccount: yup
    .string()
    .oneOf(["0", "1"], "Invalid save option")
    .required(),
});
export const expressCheckoutSchema = yup.object().shape({
  firstName: yup.string().required("Please enter receiver's first name"),
  lastName: yup.string().required("Please enter receiver's last name"),
  email: yup
    .string()
    .trim()
    .required("Please enter receiver's email address")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
      "Please enter a valid email address"
    ),

  mobile: yup
    .string()
    .required("Please enter receiver's phone number")
    .min(10, "Please enter your complete phone number"),
  area: yup.string().required("Please enter receiver's area"),
  city: yup.string().required("Please select receiver's city"),
  country: yup.string().required("Please enter receiver's country"),
  address: yup.string().required("Please enter receiver's address"),
  landmark: yup.string().required("Please enter a landmark"),
});

export const passwordSchema = yup.object().shape({
  oldPassword: yup
    .string()
    .required("Please enter your current password")
    .min(8, "Password must be at least 8 characters"),
  newPassword: yup
    .string()
    .required("Please enter a new password")
    .min(8, "Password must be at least 8 characters"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], "Passwords must match")
    .required("Please confirm your new password"),
});

export const changeEmailSchema = yup.object().shape({
  password: yup
    .string()
    .required("Please enter your current password")
    .min(8, "Password must be at least 8 characters"),

  newEmail: yup
    .string()
    .trim()
    .required("Please enter your email address")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i,
      "Please enter a valid email address"
    )
    .test(
      "check-email-exists",
      "This email is already registered",
      async function (value) {
        if (!value) return true;

        // Check format BEFORE calling API
        const isValidFormat = /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(
          value
        );
        if (!isValidFormat) return true; // format error already shown

        // Avoid repeated API calls
        if (value === lastCheckedEmail) return lastResult;

        try {
          const res = await axios.post(
            "/dashboard/customers/is-email-registered",
            { email: value }
          );

          lastCheckedEmail = value;
          lastResult = !res.data.emailAlreadyRegistered;

          return lastResult;
        } catch (err) {
          console.error("Error checking email:", err);
          return true; // Allow submission if API fails
        }
      }
    ),

  confirmEmail: yup
    .string()
    .trim()
    .required("Please confirm your email address")
    .oneOf([yup.ref("newEmail"), null], "Email addresses must match"),
});


export const emailSchema = yup.object({
  email: yup
    .string()
    .trim()
    .required("Please enter your email address")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i,
      "Please enter a valid email address"
    )
    .test(
      "check-email-exists",
      "This email is already registered, please login",
      async function (value) {
        if (!value) return true;

        // ❗ Run this test ONLY if format is valid
        const isValidFormat = /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(
          value
        );
        if (!isValidFormat) return true; // format error already handled above

        // ✔ Avoid duplicate API calls
        if (value === lastCheckedEmail) return lastResult;

        try {
          const res = await axios.post(
            "/dashboard/customers/is-email-registered",
            { email: value }
          );

          lastCheckedEmail = value;

          // If already registered → fail validation
          lastResult = !res.data.emailAlreadyRegistered;

          return lastResult;
        } catch (err) {
          console.error("Error checking email:", err);
          return true; // allow submission if API fails
        }
      }
    ),
});

export const forgotPassEmailSchema = yup.object({
  email: yup
    .string()
    .trim()
    .required("Please enter your email address")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
      "Please enter a valid email address"
    ),
});
// export const otpSchema = yup.object({
//   pin: yup
//     .string()
//     .matches(/^[0-9]{6}$/, "Please enter a valid 6 digit OTP")
//     .required("Please enter OTP"),
// });
export const otpSchema = yup.object({
  pin: yup
    .string()
    .matches(/^[0-9]{5,6}$/, "Please enter a valid OTP")
    .required("Please enter OTP"),
});

export const newPasswordSchema = yup.object({
  newPassword: yup
    .string()
    .min(8, "Password must be at least 8 characters")
    .required("Please enter a new password"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], "Passwords must match")
    .required("Please confirm your password"),
});
